/**
 * Audio utility functions for format detection and browser support
 */

/**
 * Check if an audio format is supported by the browser
 */
export const isAudioFormatSupported = (mimeType: string): boolean => {
  const audio = document.createElement('audio');
  return audio.canPlayType(mimeType) !== '';
};

/**
 * Get the MIME type from a file extension
 */
export const getMimeTypeFromExtension = (extension: string): string => {
  const mimeTypes: Record<string, string> = {
    '.mp3': 'audio/mpeg',
    '.wav': 'audio/wav',
    '.m4a': 'audio/mp4',
    '.aac': 'audio/aac',
    '.flac': 'audio/flac',
    '.ogg': 'audio/ogg',
    '.wma': 'audio/x-ms-wma'
  };
  
  return mimeTypes[extension.toLowerCase()] || '';
};

/**
 * Check if a file extension is supported for playback in the browser
 */
export const isFileExtensionPlayable = (fileName: string): boolean => {
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  const mimeType = getMimeTypeFromExtension(extension);
  
  if (!mimeType) return false;
  
  return isAudioFormatSupported(mimeType);
};

/**
 * Get unsupported file extensions that are allowed for upload but not playable
 */
export const getUnsupportedPlayableExtensions = (): string[] => {
  const extensions = ['.mp3', '.wav', '.m4a', '.aac', '.flac', '.ogg', '.wma'];
  return extensions.filter(ext => {
    const mimeType = getMimeTypeFromExtension(ext);
    return mimeType && !isAudioFormatSupported(mimeType);
  });
};

/**
 * Get a user-friendly message for unsupported audio formats
 */
export const getUnsupportedFormatMessage = (fileName: string): string => {
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  
  if (extension === '.wma') {
    return 'WMA files are not supported by web browsers. Please convert to MP3, WAV, or M4A format for playback.';
  }
  
  return 'This audio format is not supported by your browser for playback.';
};